<script lang="ts">
import type { DataTable, MediaAssetRecord } from '@codot/contracts';
import { dataFeedsApi } from '../../api/data-feeds.api';

const tables = new Map<string, { at: number; p: Promise<DataTable | null> }>();
/** A feed's approved table, fetched at most once a minute however many cards show it. */
function feedTable(feedId: string): Promise<DataTable | null> {
  const c = tables.get(feedId);
  if (c && Date.now() - c.at < 60_000) return c.p;
  const p = dataFeedsApi.get(feedId).then((f) => f.table ?? null, () => null);
  tables.set(feedId, { at: Date.now(), p });
  return p;
}
</script>

<script setup lang="ts">
/**
 * A composition drawn by the runtime itself: a paused thumbnail (no videos playing), or `playing` for a preview.
 * Letterboxed in the composition's colour.
 */
import { computed, onBeforeUnmount, onMounted, ref, shallowRef } from 'vue';
import { type CompositionDoc, compositionAssetIds } from '@codot/contracts';
import { runtimeData, runtimeSrcdoc, usePreviewSettings } from '../../utils/compositions';
import { useFonts } from '../../utils/fonts';
import { lookupMedia } from '../../utils/media-lookup';

const props = defineProps<{ doc: CompositionDoc; library?: Map<string, MediaAssetRecord>; playing?: boolean }>();
const { fonts } = useFonts();
const loaded = shallowRef<Map<string, MediaAssetRecord> | null>(null);
const lib = computed(() => props.library ?? loaded.value ?? new Map<string, MediaAssetRecord>());
const srcdoc = computed(() => runtimeSrcdoc(runtimeData(props.doc, lib.value, fonts.value ?? [])));
const frame = ref<HTMLIFrameElement | null>(null);
const settings = usePreviewSettings();
/** Feed rows by feed id, in the shape players pass them. */
const feeds = shallowRef<Record<string, Pick<DataTable, 'columns' | 'rows'>>>({});
let ready = false;

/** Like a player: its settings (location for weather) and Data Feed rows. */
function sendData(): void {
  frame.value?.contentWindow?.postMessage(JSON.parse(JSON.stringify({ codot: 1, type: 'data', data: { settings: settings.value, feeds: feeds.value } })), '*');
}
function onMessage(e: MessageEvent): void {
  if (e.source !== frame.value?.contentWindow || (e.data as { type?: string })?.type !== 'ready') return;
  ready = true;
  sendData();
  if (props.playing) frame.value?.contentWindow?.postMessage({ codot: 1, type: 'start' }, '*');
}
async function loadFeeds(): Promise<void> {
  const out: typeof feeds.value = {};
  for (const f of props.doc.feeds) {
    const t = await feedTable(f.feedId);
    if (t) out[f.feedId] = { columns: t.columns, rows: t.rows };
  }
  feeds.value = out;
  if (ready) sendData();
}
onMounted(async () => {
  window.addEventListener('message', onMessage);
  void loadFeeds();
  // Outside the Content page: just the pictures this design uses (batched with other cards on screen).
  if (!props.library) loaded.value = await lookupMedia(compositionAssetIds(props.doc)).catch(() => new Map());
});
onBeforeUnmount(() => window.removeEventListener('message', onMessage));
</script>

<template>
  <div class="absolute inset-0" :style="{ background: doc.background }">
    <iframe ref="frame" :srcdoc="srcdoc" sandbox="allow-scripts" tabindex="-1" aria-hidden="true" loading="lazy" class="pointer-events-none h-full w-full border-0" />
  </div>
</template>
